import React, { useEffect, useMemo, useState } from "react";
import api from "./api";
import { TASK_LABEL, TASKS_LABEL } from "./taskCopy";
import { importanceLabel, urgencyLabel, workTypeDisplay } from "./founderRoadmapCopy";
import "./PrioritizationWorkspace.css";

const IMPORTANCE_OPTIONS = [
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

function formatWhen(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function PrioritizationWorkspace({
  repo,
  mutateAllowed,
  entry = "prioritize",
  onOpenTask,
  onContinue,
  onCancel,
}) {
  const [view, setView] = useState(null);
  const [orderedIds, setOrderedIds] = useState([]);
  const [draftPatches, setDraftPatches] = useState({});
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [announce, setAnnounce] = useState("");
  const [showQuestion, setShowQuestion] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [previousOrder, setPreviousOrder] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [addSearch, setAddSearch] = useState("");
  const [selectedAvailable, setSelectedAvailable] = useState({});

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await api.getPriorityCenter(repo._id);
      const data = response.data;
      setView(data);
      setOrderedIds((data.active || []).map((row) => row.roadmapItemId));
      setDraftPatches({});
      setDirty(false);
      setSuggestion(null);
      setPreviousOrder(null);
      if (data.guidance?.primaryAction === "answer_next") {
        setShowQuestion(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || `Failed to load ${TASKS_LABEL}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repo._id]);

  const rowsById = useMemo(() => {
    const map = new Map();
    (view?.active || []).forEach((row) => map.set(row.roadmapItemId, row));
    return map;
  }, [view]);

  const visibleRows = useMemo(() => {
    return orderedIds
      .map((id) => {
        const base = rowsById.get(id);
        if (!base) return null;
        const patch = draftPatches[id] || {};
        return {
          ...base,
          priority: patch.priority || base.priority,
          urgency: patch.urgency || base.urgency,
        };
      })
      .filter(Boolean)
      .filter((row) => {
        if (filter === "all") return true;
        if (filter === "ready") return row.readiness.code === "ready_to_build";
        if (filter === "needs-input") return row.readiness.code === "needs_decision";
        if (filter === "blocked") return row.readiness.code === "blocked";
        return true;
      });
  }, [orderedIds, rowsById, draftPatches, filter]);

  const summary = useMemo(() => {
    const active = orderedIds.map((id) => rowsById.get(id)).filter(Boolean);
    return {
      active: active.length,
      ready: active.filter((row) => row.readiness.code === "ready_to_build").length,
      needsInput: active.filter((row) => row.readiness.code === "needs_decision").length,
      blocked: active.filter((row) => row.readiness.code === "blocked").length,
    };
  }, [orderedIds, rowsById]);

  const availableFiltered = useMemo(() => {
    const q = addSearch.trim().toLowerCase();
    return (view?.available || []).filter((task) => {
      if (!q) return true;
      return (
        task.title.toLowerCase().includes(q) ||
        String(task.outcome || "")
          .toLowerCase()
          .includes(q)
      );
    });
  }, [view, addSearch]);

  const similarInPicker = useMemo(() => {
    const ids = new Set(availableFiltered.map((task) => task.featurePlanId));
    return (view?.similarGroups || [])
      .map((group) => ({
        ...group,
        tasks: group.tasks.filter((task) => ids.has(task.featurePlanId)),
      }))
      .filter((group) => group.tasks.length > 1);
  }, [view, availableFiltered]);

  const moveRow = (id, direction) => {
    if (!mutateAllowed) return;
    setOrderedIds((current) => {
      const index = current.indexOf(id);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= current.length) return current;
      const next = [...current];
      const [row] = next.splice(index, 1);
      next.splice(target, 0, row);
      return next;
    });
    setDirty(true);
    setSuggestion(null);
    const title = rowsById.get(id)?.title || TASK_LABEL;
    setAnnounce(`Moved “${title}”. Save to keep this order.`);
  };

  const patchRow = (id, patch) => {
    if (!mutateAllowed) return;
    setDraftPatches((current) => ({
      ...current,
      [id]: { ...(current[id] || {}), ...patch },
    }));
    setDirty(true);
  };

  const save = async () => {
    if (!view || !mutateAllowed) return;
    try {
      setSaving(true);
      setError("");
      const patches = Object.entries(draftPatches).map(([roadmapItemId, patch]) => ({
        roadmapItemId,
        ...patch,
      }));
      const response = await api.savePriorityCenter(repo._id, {
        expectedPriorityRevision: view.priorityRevision,
        orderedItemIds: orderedIds,
        patches,
      });
      setView(response.data);
      setOrderedIds((response.data.active || []).map((row) => row.roadmapItemId));
      setDraftPatches({});
      setDirty(false);
      setSuggestion(null);
      setPreviousOrder(null);
      setAnnounce("Priority order saved. No sprint or engineering work was started.");
      return response.data;
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === "STALE_PRIORITY_REVISION") {
        setError("Someone else updated this list. Refreshing…");
        await load();
      } else {
        setError(err.response?.data?.error || "Could not save the priority order.");
      }
      return null;
    } finally {
      setSaving(false);
    }
  };

  const suggest = async () => {
    try {
      setSaving(true);
      setError("");
      const response = await api.suggestPriorityCenter(repo._id);
      setPreviousOrder(orderedIds);
      setOrderedIds(response.data.orderedItemIds || []);
      setSuggestion(response.data);
      setDirty(true);
      setAnnounce(
        response.data.fallback
          ? "Suggested order ready using the safe fallback. Review before saving."
          : "Suggested order ready. Review before saving."
      );
    } catch (err) {
      setError(err.response?.data?.error || "Could not suggest an order.");
    } finally {
      setSaving(false);
    }
  };

  const undoSuggestion = () => {
    if (!previousOrder) return;
    setOrderedIds(previousOrder);
    setSuggestion(null);
    setPreviousOrder(null);
    setDirty(true);
    setAnnounce("Restored your previous order.");
  };

  const answerQuestion = async (value) => {
    const question = view?.guidance?.nextQuestion;
    if (!question || !mutateAllowed) return;
    patchRow(question.roadmapItemId, { urgency: value });
    setShowQuestion(false);
    setAnnounce("Decision noted. Save the priority order to keep it.");
  };

  const openAddPanel = () => {
    setShowAddPanel(true);
    setSelectedAvailable({});
    setAddSearch("");
  };

  const addSelectedTasks = async () => {
    if (!view || !mutateAllowed) return;
    const featurePlanIds = Object.entries(selectedAvailable)
      .filter(([, selected]) => selected)
      .map(([id]) => id);
    if (!featurePlanIds.length) {
      setError(`Select at least one ${TASK_LABEL} to add.`);
      return;
    }
    try {
      setSaving(true);
      setError("");
      const response = await api.addPriorityCenterItems(repo._id, {
        expectedPriorityRevision: view.priorityRevision,
        featurePlanIds,
        idempotencyKey: `add-${Date.now()}`,
      });
      const data = response.data;
      setView(data);
      setOrderedIds((data.active || []).map((row) => row.roadmapItemId));
      setDraftPatches({});
      setDirty(false);
      setSuggestion(null);
      setShowAddPanel(false);
      setSelectedAvailable({});
      const added = data.addedCount || 0;
      const already = (data.results || []).filter((row) => row.status === "already_present").length;
      if (added) {
        setAnnounce(
          added === 1
            ? `Added 1 ${TASK_LABEL} to your priority list.`
            : `Added ${added} ${TASKS_LABEL} to your priority list.`
        );
      } else if (already) {
        setAnnounce(`Already on your priority list.`);
      }
    } catch (err) {
      setError(err.response?.data?.error || `Could not add ${TASKS_LABEL}.`);
    } finally {
      setSaving(false);
    }
  };

  const removeFromList = async (row) => {
    if (!view || !mutateAllowed || !row.featurePlanId) return;
    const ok = window.confirm(
      `Remove this ${TASK_LABEL} from the priority list?\n\nThe ${TASK_LABEL} and its history will remain available under ${TASKS_LABEL}.`
    );
    if (!ok) return;
    try {
      setSaving(true);
      setError("");
      const response = await api.removePriorityCenterItem(repo._id, row.featurePlanId, {
        expectedPriorityRevision: view.priorityRevision,
      });
      setView(response.data);
      setOrderedIds((response.data.active || []).map((item) => item.roadmapItemId));
      setDraftPatches({});
      setDirty(false);
      setAnnounce(`Removed “${row.title}” from the priority list.`);
    } catch (err) {
      setError(err.response?.data?.error || `Could not remove this ${TASK_LABEL}.`);
    } finally {
      setSaving(false);
    }
  };

  const designSprint = async () => {
    if (!mutateAllowed) return;
    try {
      setSaving(true);
      setError("");
      let latest = view;
      if (dirty) {
        latest = await save();
        if (!latest) return;
      }
      if (!latest?.prioritiesSettled && (latest?.summary?.needsInput || 0) > 0) {
        setShowQuestion(true);
        setError("Answer the open business question before designing a sprint.");
        return;
      }
      const candidates = await api.getPrioritySprintCandidates(repo._id);
      const ids = candidates.data.candidateIds || [];
      if (!ids.length) {
        setError(`Add at least one active ${TASK_LABEL} before designing a sprint.`);
        return;
      }
      const created = await api.createSprintDesign(repo._id, {
        candidateIds: ids,
        title: "Next sprint",
      });
      if (candidates.data.omitted > 0) {
        setAnnounce(
          `Sprint design started with the top ${ids.length} Tasks. ${candidates.data.omitted} more stay on the priority list.`
        );
      }
      onContinue?.(created.data.sprintDesign, {
        omitted: candidates.data.omitted || 0,
      });
    } catch (err) {
      setError(err.response?.data?.error || "Could not start sprint design.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="priority-center" data-testid="prioritization-workspace">
        <p>Loading priority list…</p>
      </section>
    );
  }

  return (
    <section className="priority-center" data-testid="prioritization-workspace">
      <div className="priority-center-head">
        <div>
          <div className="priority-eyebrow">Roadmap · Priority list</div>
          <h3>Choose what matters next</h3>
          <p>
            Keep one clear order for the work you are actively considering. Add {TASKS_LABEL} when
            they matter; leave drafts and experiments out.
          </p>
          {entry === "design" ? (
            <p className="priority-entry-note" data-testid="priority-design-gate">
              Design a sprint from this ordered list. If anything still needs a business decision,
              settle it here first.
            </p>
          ) : null}
        </div>
        <button type="button" className="secondary-button" onClick={onCancel}>
          Back to roadmap
        </button>
      </div>

      {error ? (
        <div className="error-message" role="alert">
          {error}
        </div>
      ) : null}
      <div className="visually-hidden" role="status" aria-live="polite">
        {announce}
      </div>

      {(view?.integrityWarnings || []).length ? (
        <div className="priority-integrity" role="status" data-testid="priority-integrity-warning">
          {(view.integrityWarnings || []).map((warning) => (
            <p key={`${warning.code}-${warning.workBriefId || warning.message}`}>{warning.message}</p>
          ))}
        </div>
      ) : null}

      {view?.guidance ? (
        <section className="priority-coach" aria-label="Priority guidance">
          <div>
            <strong>{view.guidance.title}</strong>
            <p>{view.guidance.body}</p>
          </div>
          {view.guidance.primaryAction === "answer_next" ? (
            <button
              type="button"
              className="primary-button"
              data-testid="priority-answer-next"
              onClick={() => setShowQuestion(true)}
              disabled={!mutateAllowed}
            >
              {view.guidance.primaryLabel}
            </button>
          ) : null}
          {view.guidance.primaryAction === "suggest" ? (
            <button
              type="button"
              className="primary-button"
              onClick={suggest}
              disabled={!mutateAllowed || saving}
            >
              {view.guidance.primaryLabel}
            </button>
          ) : null}
          {view.guidance.primaryAction === "see_blocked" ? (
            <button type="button" className="primary-button" onClick={() => setFilter("blocked")}>
              {view.guidance.primaryLabel}
            </button>
          ) : null}
          {view.guidance.primaryAction === "add_tasks" ? (
            <button
              type="button"
              className="primary-button"
              data-testid="priority-add-tasks-coach"
              onClick={openAddPanel}
              disabled={!mutateAllowed}
            >
              {view.guidance.primaryLabel}
            </button>
          ) : null}
        </section>
      ) : null}

      {showQuestion && view?.guidance?.nextQuestion ? (
        <section className="priority-question" data-testid="priority-question" aria-labelledby="priority-question-title">
          <div className="priority-eyebrow">One decision</div>
          <h4 id="priority-question-title">{view.guidance.nextQuestion.title}</h4>
          <p>{view.guidance.nextQuestion.body}</p>
          <div className="priority-answer-options">
            {view.guidance.nextQuestion.options.map((option) => (
              <button
                key={option.value}
                type="button"
                className="secondary-button"
                onClick={() => answerQuestion(option.value)}
                disabled={!mutateAllowed}
              >
                {option.label}
              </button>
            ))}
          </div>
        </section>
      ) : null}

      <div className="priority-toolbar">
        <div>
          <h4>On your priority list</h4>
          <p>Top to bottom is the order you want the team to consider.</p>
        </div>
        <label className="visually-hidden" htmlFor="priority-filter">
          Show {TASKS_LABEL}
        </label>
        <select
          id="priority-filter"
          data-testid="priority-filter"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        >
          <option value="all">All on the list</option>
          <option value="ready">Ready to build</option>
          <option value="needs-input">Needs my input</option>
          <option value="blocked">Blocked</option>
        </select>
        {mutateAllowed ? (
          <button
            type="button"
            className="secondary-button"
            data-testid="priority-add-tasks"
            onClick={openAddPanel}
            disabled={saving}
          >
            Add {TASKS_LABEL}
          </button>
        ) : null}
        <button
          type="button"
          className="secondary-button"
          data-testid="priority-suggest"
          onClick={suggest}
          disabled={!mutateAllowed || saving || !orderedIds.length}
        >
          Suggest an order
        </button>
      </div>

      {suggestion ? (
        <div className="priority-suggestion" role="status" data-testid="priority-suggestion">
          <span>{suggestion.explanation}</span>
          <button type="button" className="secondary-button" onClick={undoSuggestion}>
            Undo
          </button>
        </div>
      ) : null}

      {showAddPanel ? (
        <section className="priority-add-panel" data-testid="priority-add-panel">
          <div className="priority-add-panel-head">
            <div>
              <h4>Add {TASKS_LABEL} to your priority list</h4>
              <p>Choose what belongs here. Drafts and experiments can stay out.</p>
            </div>
            <button type="button" className="secondary-button" onClick={() => setShowAddPanel(false)}>
              Cancel
            </button>
          </div>
          <label className="priority-add-search">
            <span className="visually-hidden">Search available {TASKS_LABEL}</span>
            <input
              type="search"
              value={addSearch}
              onChange={(event) => setAddSearch(event.target.value)}
              placeholder={`Search available ${TASKS_LABEL}`}
              data-testid="priority-add-search"
            />
          </label>

          {similarInPicker.length ? (
            <div className="priority-similar" data-testid="priority-similar-groups">
              {similarInPicker.map((group) => (
                <div key={group.key} className="priority-similar-group">
                  <strong>{group.label}</strong>
                  <p>Possible duplicate—review before adding both.</p>
                  <ul>
                    {group.tasks.map((task) => (
                      <li key={task.featurePlanId}>
                        <div>
                          <strong>{task.title}</strong>
                          <p>
                            {formatWhen(task.updatedAt)
                              ? `Updated ${formatWhen(task.updatedAt)}`
                              : formatWhen(task.createdAt)
                                ? `Created ${formatWhen(task.createdAt)}`
                                : "Date unknown"}{" "}
                            · {task.stage.label}
                          </p>
                          <p>{task.outcome}</p>
                        </div>
                        <div className="priority-add-row-actions">
                          {onOpenTask ? (
                            <button
                              type="button"
                              className="secondary-button"
                              onClick={() => onOpenTask(task.featurePlanId)}
                            >
                              Open {TASK_LABEL}
                            </button>
                          ) : null}
                          <label>
                            <input
                              type="checkbox"
                              checked={Boolean(selectedAvailable[task.featurePlanId])}
                              onChange={(event) =>
                                setSelectedAvailable((current) => ({
                                  ...current,
                                  [task.featurePlanId]: event.target.checked,
                                }))
                              }
                              data-testid={`priority-available-${task.featurePlanId}`}
                            />
                            Add this {TASK_LABEL}
                          </label>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : null}

          {!availableFiltered.length ? (
            <p data-testid="priority-available-empty">No available {TASKS_LABEL.toLowerCase()} to add.</p>
          ) : (
            <ul className="priority-available-list" data-testid="priority-available-list">
              {availableFiltered.map((task) => (
                <li key={task.featurePlanId}>
                  <label className="priority-available-row">
                    <input
                      type="checkbox"
                      checked={Boolean(selectedAvailable[task.featurePlanId])}
                      onChange={(event) =>
                        setSelectedAvailable((current) => ({
                          ...current,
                          [task.featurePlanId]: event.target.checked,
                        }))
                      }
                      data-testid={`priority-available-${task.featurePlanId}`}
                    />
                    <span>
                      <strong>{task.title}</strong>
                      <span className="priority-available-meta">
                        {task.stage.label}
                        {formatWhen(task.updatedAt) ? ` · Updated ${formatWhen(task.updatedAt)}` : ""}
                      </span>
                      <span className="priority-available-outcome">{task.outcome}</span>
                    </span>
                  </label>
                  {onOpenTask ? (
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => onOpenTask(task.featurePlanId)}
                    >
                      Open {TASK_LABEL}
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}

          <div className="priority-add-panel-actions">
            <button
              type="button"
              className="primary-button"
              data-testid="priority-add-confirm"
              onClick={addSelectedTasks}
              disabled={!mutateAllowed || saving}
            >
              Add selected {TASKS_LABEL}
            </button>
          </div>
        </section>
      ) : null}

      {!visibleRows.length ? (
        <div className="priority-empty" data-testid="sprint-no-tasks">
          <p>No {TASKS_LABEL.toLowerCase()} are prioritized yet.</p>
          {mutateAllowed ? (
            <button
              type="button"
              className="primary-button"
              data-testid="priority-add-tasks-empty"
              onClick={openAddPanel}
            >
              Add {TASKS_LABEL}
            </button>
          ) : null}
        </div>
      ) : (
        <ol className="priority-list" aria-label={`Prioritized active ${TASKS_LABEL}`}>
          {visibleRows.map((row, index) => {
            const meta = workTypeDisplay({
              workType: row.workType,
              workTypeLabel: row.workTypeLabel,
            });
            const open = Boolean(expanded[row.roadmapItemId]);
            return (
              <li
                key={row.roadmapItemId}
                className={`priority-row${open ? " is-open" : ""}`}
                data-testid={`priority-row-${row.roadmapItemId}`}
                data-state={row.readiness.code}
                data-feature-plan-id={row.featurePlanId || ""}
              >
                <span className="priority-rank" aria-label={`Priority ${index + 1}`}>
                  {index + 1}
                </span>
                <div className="priority-main">
                  <div className="priority-topline">
                    <span className="priority-type">
                      <span aria-hidden="true">{meta.icon}</span> {meta.label}
                    </span>
                    <span className={`priority-status priority-status-${row.readiness.code}`}>
                      {row.readiness.label}
                    </span>
                  </div>
                  {row.featurePlanId && onOpenTask ? (
                    <button
                      type="button"
                      className="priority-title-link"
                      onClick={() => onOpenTask(row.featurePlanId)}
                      data-testid={`priority-open-${row.roadmapItemId}`}
                    >
                      {row.title}
                    </button>
                  ) : (
                    <h4 className="priority-title">{row.title}</h4>
                  )}
                  <p className="priority-outcome">{row.outcome}</p>
                  <p className={`priority-why${row.whyKind === "warning" ? " is-warning" : ""}`}>
                    {row.whyHere.replace(/^Why it is here:/i, `Why #${index + 1}:`)}
                  </p>
                </div>
                <div className="priority-controls">
                  <label className="visually-hidden" htmlFor={`importance-${row.roadmapItemId}`}>
                    Importance for {row.title}
                  </label>
                  <select
                    id={`importance-${row.roadmapItemId}`}
                    data-testid={`priority-importance-${row.roadmapItemId}`}
                    value={row.priority}
                    disabled={!mutateAllowed || saving}
                    onChange={(event) => patchRow(row.roadmapItemId, { priority: event.target.value })}
                    aria-label={`Importance: ${importanceLabel(row.priority)}`}
                  >
                    {IMPORTANCE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <div className="priority-move-group" aria-label={`Move ${row.title}`}>
                    <button
                      type="button"
                      className="priority-icon-button"
                      aria-label={`Move ${row.title} up`}
                      data-testid={`priority-up-${row.roadmapItemId}`}
                      disabled={!mutateAllowed || index === 0 || saving}
                      onClick={() => moveRow(row.roadmapItemId, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="priority-icon-button"
                      aria-label={`Move ${row.title} down`}
                      data-testid={`priority-down-${row.roadmapItemId}`}
                      disabled={!mutateAllowed || index === visibleRows.length - 1 || saving}
                      onClick={() => moveRow(row.roadmapItemId, 1)}
                    >
                      ↓
                    </button>
                  </div>
                  <button
                    type="button"
                    className="priority-icon-button"
                    aria-expanded={open}
                    aria-label={`${open ? "Hide" : "Show"} ${row.title} details`}
                    onClick={() =>
                      setExpanded((current) => ({
                        ...current,
                        [row.roadmapItemId]: !current[row.roadmapItemId],
                      }))
                    }
                  >
                    {open ? "▾" : "›"}
                  </button>
                </div>
                {open ? (
                  <div className="priority-detail">
                    <div>
                      <span className="priority-detail-label">Urgency</span>
                      {`${urgencyLabel(row.urgency)}${
                        row.urgency !== "unknown" && row.urgencySource === "user_confirmed"
                          ? " · provided by you"
                          : ""
                      }`}
                    </div>
                    <div>
                      <span className="priority-detail-label">Blocker</span>
                      {row.blocker || "None"}
                    </div>
                    {meta.needsType ? (
                      <div>
                        <span className="priority-detail-label">Work type</span>
                        Needs a type — set this on the Roadmap item when you know it.
                      </div>
                    ) : null}
                    {mutateAllowed ? (
                      <div className="priority-detail-actions">
                        <button
                          type="button"
                          className="secondary-button"
                          data-testid={`priority-remove-${row.roadmapItemId}`}
                          onClick={() => removeFromList(row)}
                          disabled={saving}
                        >
                          Remove from priority list
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}

      {(view?.archived || []).length ? (
        <details className="priority-finished" data-testid="priority-archived">
          <summary>
            Completed and archived {TASKS_LABEL} · {view.archived.length}
          </summary>
          <p>
            Finished work stays available for reference but will not be proposed for a sprint by
            default.
          </p>
          <ul>
            {view.archived.map((row) => (
              <li key={row.roadmapItemId}>{row.title}</li>
            ))}
          </ul>
        </details>
      ) : null}

      <div className="priority-footer">
        <div>
          <strong data-testid="priority-summary">
            {summary.active} on the list · {summary.ready} ready · {summary.needsInput} need input ·{" "}
            {summary.blocked} blocked
            {typeof view?.summary?.available === "number"
              ? ` · ${view.summary.available} available`
              : ""}
          </strong>
          <span>Saving this order does not start work or change a sprint.</span>
          {!mutateAllowed ? <span>View only — ask an editor to change the order.</span> : null}
        </div>
        <button
          type="button"
          className="secondary-button"
          data-testid="priority-save"
          onClick={save}
          disabled={!mutateAllowed || saving || !dirty}
        >
          Save priority order
        </button>
        <button
          type="button"
          className="primary-button"
          data-testid="priority-design-sprint"
          onClick={designSprint}
          disabled={!mutateAllowed || saving || !orderedIds.length}
        >
          Design sprint from this list
        </button>
      </div>
    </section>
  );
}

export default PrioritizationWorkspace;
